import React, { PropsWithChildren, useEffect } from 'react';

import { Text } from '@opendoor/bricks-next';
import { Box, Button } from '@opendoor/bricks/core';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';

import { useMutation, useQuery } from 'platform/apollo';
import { useAuth } from 'platform/auth-fe';
import { ContextSheet, Popover } from 'platform/bricks/Modal';
import { GetCustomerTOSStatus } from 'platform/graphql/__generated__/GetCustomerTOSStatus';
import { SetCustomerTOSAccepted } from 'platform/graphql/__generated__/SetCustomerTOSAccepted';
import { GET_CUSTOMER_TOS_STATUS, SET_CUSTOMER_TOS_ACCEPTED } from 'platform/graphql/TOSStatus';
import { useObservability } from 'platform/observability';
import { LegacyExternalLink } from 'platform/remix-run/react';

import { useBrowserCompatibilityData } from 'shared/helpers/globals';

/**
 * Hook to retrieve data and functions to use with the TOS Modal
 * @throws error if hook is used outside of the scope of TOSModalContext
 * @returns TOSModalContext data
 */
export const useTOSContext = () => {
  const context = React.useContext(TOSContext);

  if (context === undefined) {
    throw new Error(`useTOSContext must be used within TOS Context`);
  }
  return context;
};

export interface TOSContext {
  /**
   * Gives pages the ability to override if the TOS modal should be shown
   * Helpful for pages like loading,error when it should never be shown
   */
  forceHideTOSModal: (hideModal: boolean) => void;
  /**
   * Determines if the TOS modal should be shown
   */
  showTOSModal: boolean;
  /**
   * adds a loading state to the customer TOS button on acceptance
   */
  isTOSAcceptanceLoading: boolean;
  /**
   * Called when the customer accepts the TOS and updates the status
   */
  customerAcceptsTOS: () => void;
}

export const TOSContext = React.createContext({
  forceHideTOSModal: (_hideModal: boolean) => {},
  showTOSModal: false,
  isTOSAcceptanceLoading: false,
  customerAcceptsTOS: () => {},
});

export const TOSProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { trackError } = useObservability();
  const { state, user } = useAuth();
  const [showTOSModal, setShowTOSModal] = React.useState(false);
  const [isTOSAcceptanceLoading, setCustomerAcceptedLoading] = React.useState(false);
  const [tosModalOverriden, setTOSModalOverriden] = React.useState(false);

  /**
   * Determines if the Customer should see the TOS modal
   * Skips checking the TOSStatusEndpoint if it's a webview or use is not authenticated
   * We store the result in state - tosUpdateRequired and update the state if customer accepts TOS - so we don't show the TOS modal again while the backend updates the status
   */
  const [tosUpdateRequired, settosUpdateRequired] = React.useState(false);

  const { isOpendoorAppEvoWebView } = useBrowserCompatibilityData();
  const { data, loading } = useQuery<GetCustomerTOSStatus>(GET_CUSTOMER_TOS_STATUS, {
    skip: state !== 'authenticated' || isOpendoorAppEvoWebView || user?.impersonated,
    context: { linkContext: { team: 'consumer-platform' } },
  });
  const updateRequired = !loading && !!data?.customerTos.getTOSStatus?.updateRequired;
  useEffect(() => {
    settosUpdateRequired(updateRequired);
  }, [updateRequired]);

  useEffect(() => {
    if (tosUpdateRequired && !tosModalOverriden) {
      setShowTOSModal(true);
    }

    return () => setShowTOSModal(false);
  }, [tosUpdateRequired, tosModalOverriden]);

  /**
   * This function gives pages the ability to override if the TOS modal should be shown
   * Helpful for pages like loading or error when it should never be shown
   */
  const forceHideTOSModal = (hideModal: boolean) => {
    if (hideModal) {
      setShowTOSModal(!hideModal);
      // this prevents us from pre-maturely flashing the modal on the loading page
      setTOSModalOverriden(true);
    } else {
      setShowTOSModal(tosUpdateRequired);
      setTOSModalOverriden(false);
    }
  };
  /**
   * Customer accepts the TOS and updates the status
   */
  const [setCustomerTOSAccepted] = useMutation<SetCustomerTOSAccepted>(SET_CUSTOMER_TOS_ACCEPTED, {
    context: { linkContext: { team: 'consumer-platform' } },
  });

  const customerAcceptsTOS = () => {
    // ensures a customer can't click the button multiple times so we're setting the loading state to true
    setCustomerAcceptedLoading(true);
    // tracks an error if the mutation takes more than 10 seconds
    const timeoutId = setTimeout(() => {
      trackError(
        'set customer TOS accepted error',
        new Error('setCustomerTOSAccepted mutation took more than 10 seconds'),
      );
    }, 10000);

    setCustomerTOSAccepted()
      .then(() => {
        setShowTOSModal(false);
        settosUpdateRequired(false);
      })
      .catch((error) => {
        trackError('set customer TOS accepted error', error);
      })
      .finally(() => {
        setCustomerAcceptedLoading(false);
        // if setCustomerTOSAccepted is successful or fails before the timeout clear the timeout
        clearTimeout(timeoutId);
      });
  };

  return (
    <TOSContext.Provider
      value={{
        forceHideTOSModal,
        showTOSModal,
        isTOSAcceptanceLoading,
        customerAcceptsTOS,
      }}
    >
      {children}
    </TOSContext.Provider>
  );
};

interface TOSContentProps {
  setAccepted: React.MouseEventHandler<HTMLButtonElement>;
  isTOSAcceptanceLoading: boolean;
  isMobile: boolean;
}

const TOSModalContent: React.FC<TOSContentProps> = ({
  setAccepted,
  isTOSAcceptanceLoading,
  isMobile,
}) => (
  <Box maxWidth={isMobile ? 'initial' : '450px'}>
    <Box m={['32px 24px 16px 24px', '48px 48px 16px 48px']}>
      <Text
        typography="$headerSmall"
        tag="h1"
        whiteSpace="normal"
        textAlign="left"
        id="tos-modal-header"
      >
        We've updated
        <br />
        our terms
      </Text>
    </Box>
    <Box margin={['0px 24px 40px 24px', '0px 48px 48px 48px']}>
      <Text typography="$bodyMedium" tag="span" backgroundColor="$backgroundSecondary">
        We encourage you to review our updated{' '}
        <LegacyExternalLink
          aria-label=""
          href="https://www.opendoor.com/terms"
          display="inline"
          target="_blank"
          analyticsName="tos-modal-link"
          color="warmgrey900"
          textDecoration="underline"
          data-testid="tos-link"
          whiteSpace="nowrap"
          trackImpression={true}
        >
          Terms of Service
        </LegacyExternalLink>
        . These Terms contain an arbitration agreement under which both you and Opendoor agree to
        resolve disputes through final and binding arbitration on an individual basis, and not by
        way of a lawsuit in court or a class action of any kind.
      </Text>
      <Box mt={[4, 5]}>
        <Text typography="$bodyMedium" tag="span" backgroundColor="$backgroundSecondary">
          By continuing, you agree to the updated Terms.
        </Text>
      </Box>
      <Button
        mt={[8, 9]}
        onClick={setAccepted}
        aria-label={'Accepts the Terms and Conditions'}
        analyticsName="accept-tos"
        data-testid="accept-tos-button"
        trackImpression={true}
        loading={isTOSAcceptanceLoading}
      >
        Continue
      </Button>
    </Box>
  </Box>
);

export const TOSModal: React.FC = () => {
  const { showTOSModal, customerAcceptsTOS, isTOSAcceptanceLoading } = useTOSContext();
  const { isMobile } = useDevice();
  return isMobile ? (
    <ContextSheet
      id="tos-modal"
      themeProps={{ theme: 'novo' }}
      hasPadding={false}
      analyticsName="TOS-Modal"
      aria-label=""
      isOpen={showTOSModal}
      contentBgColor="warmgrey100"
      // purposefully set onRequestClose to undefined to prevent the user from closing the by using the close button
      onRequestClose={undefined}
      showScrollbar={false}
    >
      <TOSModalContent
        setAccepted={customerAcceptsTOS}
        isTOSAcceptanceLoading={isTOSAcceptanceLoading}
        isMobile={isMobile}
      />
    </ContextSheet>
  ) : (
    <Popover
      id="tos-modal"
      themeProps={{ theme: 'novo' }}
      hasPadding={false}
      showDividers={false}
      analyticsName="TOS-Modal"
      aria-label=""
      isOpen={showTOSModal}
      contentBgColor="warmgrey100"
      // purposefully set onRequestClose to undefined to prevent the user from closing the by using the close button
      onRequestClose={undefined}
    >
      <TOSModalContent
        setAccepted={customerAcceptsTOS}
        isTOSAcceptanceLoading={isTOSAcceptanceLoading}
        isMobile={isMobile}
      />
    </Popover>
  );
};
